/**
 * 香港接口 HTTP 请求封装
 * 
 * 【重要】此文件用于香港地区接口请求，走代理服务
 * 
 * 环境配置（env/.env.*）：
 * - VITE_HK_BASE_URL: 香港接口基础地址
 * - VITE_HK_PROXY_ENABLE: 是否启用代理（H5 环境）
 * - VITE_HK_PROXY_PREFIX: 代理前缀路径
 * - VITE_HK_BASE_URL2: 香港 CMS 接口基础地址
 * - VITE_HK_PROXY_ENABLE2: 是否启用香港 CMS 代理（H5 环境）
 * - VITE_HK_PROXY_PREFIX2: 香港 CMS 代理前缀路径
 * 
 * 特点：
 * 1. H5 环境下通过本地代理转发请求（避免跨域和 Cloudflare 拦截）
 * 2. 使用完整的 HTTP URL，绕过 uni.addInterceptor 拦截
 * 3. 独立的 token 管理（hk_token）
 * 4. 直接使用 uni.request，不经过项目的请求拦截器
 * 
 * 与 http.ts 的区别：
 * - http.ts: 直接访问主服务（VITE_SERVER_BASEURL），经过统一拦截器
 * - httpHk.ts: 访问香港服务（VITE_HK_BASE_URL），独立请求处理
 */

import { prepareRequestAuth } from '@/interceptors/request'

interface RequestOptions {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  data?: Record<string, any>
  header?: Record<string, any>
  timeout?: number
}

export interface ResponseData<T = any> {
  code: number
  msg?: string
  message?: string
  data: T,
  success: boolean
}

/**
 * 核心请求方法
 */
async function request<T = any>(options: RequestOptions): Promise<ResponseData<T>> {
  const { url, method = 'GET', data, header = {}, timeout = 60000 } = options
  
  // 构建完整的请求 URL
  // 关键：使用完整的 http 地址，这样会绕过 uni.addInterceptor 的拦截
  let fullUrl = url
  
  // 如果 URL 不是以 http 开头，需要拼接基础地址
  if (!url.startsWith('http')) {
    // #ifdef H5
    // H5 环境下，如果启用了代理，使用本地代理地址（完整 URL）
    const hkProxyEnable = import.meta.env.VITE_HK_PROXY_ENABLE !== 'false'
    if (hkProxyEnable) {
      // 使用 window.location.origin 构建完整的本地代理 URL
      const proxyPrefix = import.meta.env.VITE_HK_PROXY_PREFIX || '/api'
      // 直接拼接，不做任何处理
      fullUrl = `${window.location.origin}${proxyPrefix}${url}`
    } else {
      fullUrl = (import.meta.env.VITE_HK_BASE_URL || 'https://dev.yaichi.com') + url
    }
    // #endif
    
    // #ifndef H5
    // 非 H5 环境，直接使用完整的香港接口地址
    fullUrl = (import.meta.env.VITE_HK_BASE_URL || 'https://dev.yaichi.com') + url
    // #endif
  }

  const hkToken = await prepareRequestAuth('tokenHK')
  
  console.log('HK API 请求:', {
    url: fullUrl,
    method,
    hasToken: !!hkToken,
    tokenPreview: hkToken ? hkToken : 'none'
  })

  return new Promise((resolve, reject) => {
    uni.request({
      url: fullUrl,
      method,
      data,
      header: {
        ...header,
        ...(hkToken ? { 'Authorization': `Bearer ${hkToken}` } : {}),
        'Content-Type': 'application/json',
      },
      timeout,
      success: (res: any) => {
        console.log('HK API 响应:', {
          statusCode: res.statusCode,
          data: res.data
        })
        
        if (res.statusCode >= 200 && res.statusCode < 300) {
          const responseData = res.data as ResponseData<T>
          resolve({
            ...responseData,
            msg: responseData.msg || responseData.message,
            message: responseData.message || responseData.msg,
          })
        //   if (responseData.success) {
            
        //   } else {
        //     const errorMsg = responseData.msg || '请求失败'
        //     console.error('HK API 业务错误:', errorMsg, responseData)
        //     uni.showToast({
        //       icon: 'none',
        //       title: errorMsg,
        //     })
        //     reject(responseData)
        //   }
        } else {
          console.error('HK API HTTP 错误:', res.statusCode, res)
        //   uni.showToast({
        //     icon: 'none',
        //     title: `请求错误 (${res.statusCode})`,
        //   })
          reject(res)
        }
      },
      fail: (err) => {
        //console.error('HK API 网络错误:', err)
        // uni.showToast({
        //   icon: 'none',
        //   title: '网络错误，请检查网络连接',
        // })
        reject(err)
      },
    })
  })
}

/**
 * GET 请求
 */
export function httpHkGet<T = any>(url: string, data?: Record<string, any>, header?: Record<string, any>) {
  return request<T>({ url, method: 'GET', data, header })
}

/**
 * 香港 CMS GET 请求
 */
export function httpHkGet2<T = any>(url: string, data?: Record<string, any>, header?: Record<string, any>) {
  let fullUrl = url

  if (!url.startsWith('http')) {
    // #ifdef H5
    const hkProxyEnable2 = import.meta.env.VITE_HK_PROXY_ENABLE2 !== 'false'
    if (hkProxyEnable2) {
      const proxyPrefix2 = import.meta.env.VITE_HK_PROXY_PREFIX2 || '/api'
      fullUrl = `${window.location.origin}${proxyPrefix2}${url}`
    }
    else {
      fullUrl = (import.meta.env.VITE_HK_BASE_URL2 || '') + url
    }
    // #endif

    // #ifndef H5
    fullUrl = (import.meta.env.VITE_HK_BASE_URL2 || '') + url
    // #endif
  }

  return request<T>({ url: fullUrl, method: 'GET', data, header })
}

/**
 * POST 请求
 */
export function httpHkPost<T = any>(url: string, data?: Record<string, any>, header?: Record<string, any>) {
  return request<T>({ url, method: 'POST', data, header })
}

/**
 * 香港 CMS POST 请求
 */
export function httpHkPost2<T = any>(url: string, data?: Record<string, any>, header?: Record<string, any>) {
  let fullUrl = url

  if (!url.startsWith('http')) {
    // #ifdef H5
    const hkProxyEnable2 = import.meta.env.VITE_HK_PROXY_ENABLE2 !== 'false'
    if (hkProxyEnable2) {
      const proxyPrefix2 = import.meta.env.VITE_HK_PROXY_PREFIX2 || '/api'
      fullUrl = `${window.location.origin}${proxyPrefix2}${url}`
    }
    else {
      fullUrl = (import.meta.env.VITE_HK_BASE_URL2 || '') + url
    }
    // #endif

    // #ifndef H5
    fullUrl = (import.meta.env.VITE_HK_BASE_URL2 || '') + url
    // #endif
  }

  return request<T>({ url: fullUrl, method: 'POST', data, header })
}

/**
 * PUT 请求
 */
export function httpHkPut<T = any>(url: string, data?: Record<string, any>, header?: Record<string, any>) {
  return request<T>({ url, method: 'PUT', data, header })
}

/**
 * DELETE 请求
 */
export function httpHkDelete<T = any>(url: string, data?: Record<string, any>, header?: Record<string, any>) {
  return request<T>({ url, method: 'DELETE', data, header })
}

// 默认导出
export const httpHk = {
  get: httpHkGet,
  get2: httpHkGet2,
  post: httpHkPost,
  post2: httpHkPost2,
  put: httpHkPut,
  delete: httpHkDelete,
}
